import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Truck, MapPin, ChevronRight, MessageCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../CartContext';
import { FREE_SHIPPING_THRESHOLD, SHIPPING_COST, SHIPPING_DAYS_LABEL, formatTl } from '../constants/storePolicies';
import { HavaleTrustBlock } from '../components/HavaleTrustBlock';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface FormData {
  email: string; phone: string; firstName: string; lastName: string;
  address: string; city: string; district: string; postalCode: string;
}

const initialFormData: FormData = {
  email: '', phone: '', firstName: '', lastName: '',
  address: '', city: '', district: '', postalCode: '',
};

export function Checkout() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptKvkk, setAcceptKvkk] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'shopier' | 'whatsapp'>('shopier');

  const subtotal = cartTotal;
  const shippingCost = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const total = subtotal + shippingCost;

  useEffect(() => {
    try {
      const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
      if (!kullanici) return;
      setFormData(prev => ({
        ...prev,
        email: kullanici.email || prev.email,
        phone: kullanici.telefon || prev.phone,
        firstName: kullanici.ad || prev.firstName,
        lastName: kullanici.soyad || prev.lastName,
        address: kullanici.adres || prev.address,
        city: kullanici.sehir || kullanici.il || prev.city,
        district: kullanici.ilce || prev.district,
        postalCode: kullanici.postaKodu || kullanici.posta_kodu || prev.postalCode,
      }));
    } catch {
      // ignore
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptTerms || !acceptKvkk) { toast.error('Lütfen kullanım koşullarını ve KVKK metnini onaylayın'); return; }
    if (!formData.email || !formData.phone || !formData.firstName || !formData.lastName) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    if (!formData.address || !formData.city || !formData.district) {
      toast.error('Lütfen teslimat adresini eksiksiz girin');
      return;
    }
    if (cartItems.length === 0) { toast.error('Sepetiniz boş'); return; }

    setSubmitting(true);
    const toastId = toast.loading('Siparişiniz hazırlanıyor...');

    try {
      const kullanici = JSON.parse(localStorage.getItem('kullanici') || 'null');
      const adSoyad = `${formData.firstName} ${formData.lastName}`.trim();
      const adres = `${adSoyad}, ${formData.address}, ${formData.district}, ${formData.city}${formData.postalCode ? ` ${formData.postalCode}` : ''}`;
      const kargoBilgi = `Standart Kargo (Yurtiçi Kargo) — ${SHIPPING_DAYS_LABEL}`;

      const res = await fetch(`${API_URL}/api/siparis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kullaniciId: kullanici?.id || null,
          urunler: cartItems.map(item => ({
            urunId: item.id,
            ad: item.name,
            fiyat: item.price,
            adet: item.quantity,
            beden: item.size || '',
            renk: item.color || '',
          })),
          toplamTutar: total,
          adres,
          telefon: formData.phone,
          adSoyad,
          email: formData.email,
          kargoBilgi,
          odemeTipi: paymentMethod === 'shopier' ? 'shopier' : 'whatsapp_havale',
        }),
      });
      const data = await res.json();

      if (res.ok && data.siparisId) {
        if (paymentMethod === 'shopier') {
          const checkoutRes = await fetch(`${API_URL}/api/shopier/checkout-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              orderId: data.siparisId,
              items: cartItems.map(item => ({
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: item.quantity,
                image: item.image,
              })),
            }),
          });
          const checkoutData = await checkoutRes.json();
          if (checkoutRes.ok && checkoutData?.odemeSayfasi) {
            toast.dismiss(toastId);
            clearCart();
            window.location.href = checkoutData.odemeSayfasi;
            return;
          }
          toast.dismiss(toastId);
          toast.error(checkoutData?.hata || 'Shopier ödeme linki oluşturulamadı');
          navigate('/odeme-basarisiz');
          return;
        }

        if (data.whatsappUrl) {
          toast.dismiss(toastId);
          clearCart();
          window.open(data.whatsappUrl, '_blank', 'noopener,noreferrer');
          navigate('/odeme-basarili', { state: { siparisId: data.siparisId } });
          return;
        }

        toast.dismiss(toastId);
        toast.error(data.mesaj || 'WhatsApp yönlendirmesi yapılamadı');
        clearCart();
        navigate('/odeme-basarili', { state: { siparisId: data.siparisId } });
      } else {
        toast.dismiss(toastId);
        toast.error(data.mesaj || 'Sipariş oluşturulamadı');
        navigate('/odeme-basarisiz');
      }
    } catch {
      toast.dismiss(toastId);
      navigate('/odeme-basarisiz');
    }
    setSubmitting(false);
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-xl mb-4">Sepetiniz boş</p>
            <a href="/" className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold">Alışverişe Başla</a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 md:gap-4">
              <div className="flex items-center gap-2">
                <div className="size-8 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold">✓</div>
                <span className="hidden md:inline text-sm font-medium text-gray-700">Sepet</span>
              </div>
              <ChevronRight className="size-5 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="size-8 bg-pink-600 text-white rounded-full flex items-center justify-center font-semibold">2</div>
                <span className="hidden md:inline text-sm font-medium text-gray-900">Sipariş Bilgileri</span>
              </div>
              <ChevronRight className="size-5 text-gray-400" />
              <div className="flex items-center gap-2">
                <div className="size-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-semibold">3</div>
                <span className="hidden md:inline text-sm font-medium text-gray-500">Ödeme</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="size-8 bg-pink-100 rounded-full flex items-center justify-center">
                      <span className="text-pink-600 font-bold">1</span>
                    </div>
                    İletişim Bilgileri
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">E-posta Adresi *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="ornek@email.com" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Telefon Numarası *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="0 (5__) ___ __ __" className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <div className="size-8 bg-pink-100 rounded-full flex items-center justify-center">
                      <MapPin className="size-4 text-pink-600" />
                    </div>
                    Teslimat Adresi
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">Hesabınızdaki adres otomatik doldurulur; isterseniz buradan değiştirebilirsiniz.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Ad *</label>
                      <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Soyad *</label>
                      <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Adres *</label>
                      <textarea name="address" value={formData.address} onChange={handleInputChange} rows={3} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">İl *</label>
                      <select name="city" value={formData.city} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required>
                        <option value="">Seçiniz</option>
                        <option value="istanbul">İstanbul</option>
                        <option value="ankara">Ankara</option>
                        <option value="izmir">İzmir</option>
                        <option value="bursa">Bursa</option>
                        <option value="antalya">Antalya</option>
                        <option value="adana">Adana</option>
                        <option value="gaziantep">Gaziantep</option>
                        <option value="konya">Konya</option>
                        <option value="diger">Diğer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">İlçe *</label>
                      <input type="text" name="district" value={formData.district} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" required />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">Posta Kodu</label>
                      <input type="text" name="postalCode" value={formData.postalCode} onChange={handleInputChange} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg outline-none focus:border-pink-500" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="size-8 bg-pink-100 rounded-full flex items-center justify-center">
                      <Truck className="size-4 text-pink-600" />
                    </div>
                    Kargo Seçimi
                  </h2>
                  <div className="flex items-center justify-between p-4 border-2 border-pink-600 bg-pink-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <input type="radio" name="shipping" checked readOnly className="size-5 text-pink-600" />
                      <div>
                        <div className="font-semibold text-gray-900">Standart Kargo (Yurtiçi Kargo)</div>
                        <div className="text-sm text-gray-600">{SHIPPING_DAYS_LABEL} · {formatTl(FREE_SHIPPING_THRESHOLD)} TL üzeri ücretsiz</div>
                      </div>
                    </div>
                    <span className={`font-bold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {shippingCost === 0 ? 'Ücretsiz' : `₺${SHIPPING_COST}`}
                    </span>
                  </div>
                </div>

                <HavaleTrustBlock />

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Ödeme Yöntemi</h2>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer border-pink-600 bg-pink-50">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={paymentMethod === 'shopier'}
                          onChange={() => setPaymentMethod('shopier')}
                          className="size-4"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">Shopier ile Güvenli Ödeme</p>
                          <p className="text-sm text-gray-600">Kart / Shopier ödeme ekranına yönlendirilirsiniz.</p>
                        </div>
                      </div>
                      <CreditCard className="size-5 text-pink-600" />
                    </label>

                    <label className="flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer border-gray-200">
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={paymentMethod === 'whatsapp'}
                          onChange={() => setPaymentMethod('whatsapp')}
                          className="size-4"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">WhatsApp + Havale</p>
                          <p className="text-sm text-gray-600">Sipariş sonrası WhatsApp üzerinden IBAN bilgisi gelir.</p>
                        </div>
                      </div>
                      <MessageCircle className="size-5 text-green-600" />
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} className="size-5 text-pink-600 mt-0.5" required />
                      <span className="text-sm text-gray-700">
                        <a href="/yasal/kullanim-kosullari" target="_blank" rel="noreferrer" className="text-pink-600 hover:underline">Kullanım Koşulları</a>
                        {' '}nı ve{' '}
                        <a href="/yasal/mesafeli-satis-sozlesmesi" target="_blank" rel="noreferrer" className="text-pink-600 hover:underline">Mesafeli Satış Sözleşmesi</a>
                        {' '}ni okudum, kabul ediyorum. *
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={acceptKvkk} onChange={(e) => setAcceptKvkk(e.target.checked)} className="size-5 text-pink-600 mt-0.5" required />
                      <span className="text-sm text-gray-700">
                        Kişisel verilerimin işlenmesine ilişkin{' '}
                        <a href="/yasal/kvkk" target="_blank" rel="noreferrer" className="text-pink-600 hover:underline">KVKK Aydınlatma Metni</a>
                        {' '}ni okudum, kabul ediyorum. *
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg p-6 shadow-sm sticky top-24">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Sipariş Özeti</h2>
                  <div className="mb-6 pb-6 border-b space-y-3">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex gap-3">
                        <img src={item.image} alt={item.name} className="size-16 object-cover rounded" />
                        <div className="flex-1 text-sm">
                          <p className="font-medium text-gray-900">{item.name}</p>
                          <p className="text-gray-600">
                            {item.size && `Beden: ${item.size}`} {item.color && `• ${item.color}`} {item.quantity > 1 && `(x${item.quantity})`}
                          </p>
                          <p className="font-semibold text-pink-600">₺{(item.price * item.quantity).toLocaleString('tr-TR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 mb-6 pb-6 border-b">
                    <div className="flex justify-between text-gray-600">
                      <span>Ara Toplam</span>
                      <span>₺{subtotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Kargo</span>
                      <span className={shippingCost === 0 ? 'text-green-600 font-semibold' : ''}>
                        {shippingCost === 0 ? 'Ücretsiz' : `₺${shippingCost.toFixed(2)}`}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-gray-900 mb-6">
                    <span>Toplam</span>
                    <span className="text-pink-600">₺{total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400"
                  >
                    {paymentMethod === 'shopier' ? <CreditCard className="size-5" /> : <MessageCircle className="size-5" />}
                    {submitting ? 'Hazırlanıyor...' : (paymentMethod === 'shopier' ? 'Shopier ile Devam Et' : 'WhatsApp ile Siparişi Tamamla')}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
